import { useEffect, useState } from 'react';
import Spinner from 'react-spinkit';
import Table from '../Tables/Table';
import Tooltip from '../../components/Tooltip';
import { getDefaultSchoolYear } from '../reportUtilities';
import styles from './StudentWatchList.module.scss';
import generalStyles from './SchoolReport.module.scss';
import { ReportStudent } from '../../../peekapak-types/ReportDataExchangeTypes';
import { UserProfileType } from '../../../peekapak-types/DataProtocolTypes';
import { getMoodDisplayName, Mood, SecondaryMood } from '../../../peekapak-types/Mood';

interface Props {
  journalsMap: Record<string, unknown>;
  journalsMapReady: boolean;
  selectedSchoolYear: string;
  teacherStudentMapReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  userList: UserProfileType[];
  userListReady: boolean;
}
const StudentWatchList = ({
  journalsMap,
  journalsMapReady,
  selectedSchoolYear,
  teacherStudentMapReady,
  teacherStudentMap,
  userList,
}: Props) => {
  const [myState, setMyState] = useState({
    dataMap: {},
    dataMapReady: false,
  });

  // const prevJournalsMapReady = useRef(journalsMapReady);

  // useEffect(() => {
  //   if (prevJournalsMapReady.current && !journalsMapReady) {
  //     setMyState((prevState) => ({
  //       ...prevState,
  //       dataMapReady: false,
  //     }));
  //   }

  //   prevJournalsMapReady.current = prevJournalsMapReady;
  // }, [journalsMapReady]);

  useEffect(() => {
    setMyState((prevState) => ({
      ...prevState,
      dataMap: {},
      dataMapReady: false,
    }));
  }, [journalsMap]);

  useEffect(() => {
    // look only at last 7 days of journals
    const fromDate = Date.now() - 1000 * 60 * 60 * 24 * 7;

    if (journalsMapReady && teacherStudentMapReady && !myState.dataMapReady) {
      buildData();
    }

    function buildData() {
      const newMap = {};
      const currentSchoolYearRange = getDefaultSchoolYear();
      Object.keys(teacherStudentMap).forEach((teacherKey) => {
        const newStudentsArray = teacherStudentMap[teacherKey].map((s) => {
          const toStoreJournals = journalsMap[s.userId]
            ? journalsMap[s.userId].filter((j) => j.time > fromDate && selectedSchoolYear === currentSchoolYearRange)
            : undefined;
          return {
            ...s,
            journals: toStoreJournals,
          };
        });

        newMap[teacherKey] = newStudentsArray;
      });

      setMyState((prevState) => ({
        ...prevState,
        dataMap: newMap,
        dataMapReady: true,
      }));
    }
  }, [teacherStudentMap, journalsMap, journalsMapReady, myState.dataMapReady]);

  return (
    <div className={styles.studentWatchList}>
      <h4 className={generalStyles.tableHeader}>Students with the most down moods from last 7 days</h4>
      <Table tableHeader={[{ title: 'Full Name' }, { title: 'Teacher' }, { title: 'Downmoods' }]}>{getContent()}</Table>
    </div>
  );

  function getContent() {
    if (!myState.dataMapReady) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>
              Loading
              <Spinner name='line-scale' />
            </div>
          </td>
        </tr>
      );
    }

    return <>{getStudentWatchList()}</>;
  }

  function getDownMoodsStrings() {
    const elementaryDownMoods = [Mood.SAD, Mood.ANGRY, Mood.NERVOUS];
    const secondaryDownMoods = [
      ...Object.values(SecondaryMood.SAD),
      ...Object.values(SecondaryMood.ANGRY),
      ...Object.values(SecondaryMood.SCARED),
    ];

    const expandedSecondaryMoods = secondaryDownMoods.map((mood) => getMoodDisplayName(mood));

    return [...elementaryDownMoods, ...expandedSecondaryMoods];
  }

  function getStudentWatchList() {
    /*
     *{ NULL = 0, HAPPY = 1, EXCITED = 2, SAD = 3, ANGRY = 4, CALM =5 , NERVOUS = 6};
     */
    const emailNameMap = {};
    userList
      .filter((e) => e.type === 'Teacher' || e.type === 'SchoolAdmin' || e.type === 'DistrictAdmin')
      .forEach((u) => {
        emailNameMap[u.email] = u;
      });

    const dataList = [];

    Object.keys(myState.dataMap).forEach((k) => {
      const studentArray = myState.dataMap[k];

      let i = 0;
      while (i < studentArray.length) {
        if (studentArray[i].journals) {
          const downMoods = studentArray[i].journals.reduce(
            (acc, entry) => (getDownMoodsStrings().includes(entry.mood) ? acc + 1 : acc),
            0,
          );

          const found = dataList.find(
            (e) => e.userId === studentArray[i].userId, // eslint-disable-line no-loop-func
          );

          if (!found) {
            dataList.push({
              ...studentArray[i],
              downMoods,
              teacher: emailNameMap[k],
            });
          }
        }

        i += 1;
      }
    });

    const sorted = [...dataList].sort((a, b) => b.downMoods - a.downMoods);
    const nonZero = sorted.filter((e) => e.downMoods > 0);
    const limit = Math.min(nonZero.length, 5);

    let i = 0;
    const toReturn = [];
    while (i < limit) {
      toReturn.push(nonZero[i]);
      i += 1;
    }

    if (toReturn.length === 0) {
      return (
        <tr>
          <td className={generalStyles.tdForProgressMessage}>
            <div className={generalStyles.progressMessage}>No data available</div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {toReturn.map((e, idx) => {
          const studentName = `${e.firstName || ''} ${e.lastName || ''}`.trim();
          const teacherName = `${e?.teacher?.firstName || ''} ${e?.teacher?.lastName || ''}`.trim();
          const studentTooltip = e.username || e.userId || null;
          const teacherTooltip = e?.teacher?.email || e?.teacher?.userId || null;

          return (
            <tr key={`${e.firstName}-${idx}`}>
              <td>
                {studentTooltip ? (
                  <Tooltip content={studentTooltip} direction='bottom' color='blue'>
                    <span>{studentName}</span>
                  </Tooltip>
                ) : (
                  studentName
                )}
              </td>
              <td>
                {teacherTooltip ? (
                  <Tooltip content={teacherTooltip} direction='bottom' color='blue'>
                    <span>{teacherName}</span>
                  </Tooltip>
                ) : (
                  teacherName
                )}
              </td>
              <td>{e.downMoods}</td>
            </tr>
          );
        })}
      </>
    );
  }
};

export default StudentWatchList;
